import type { Platform } from "../api/types";
import type { DeviceBridge, SecureStore, StorageInfo } from "./DeviceBridge";

// Web/PWA/kiosk implementation of the DeviceBridge.
//
// - secureStore: localStorage (best-effort; native shells override with
//   Android Keystore / EncryptedSharedPrefs etc.)
// - media cache: Cache Storage API, with in-memory object-URL reuse so the
//   same media_id doesn't leak a new blob URL on every resolve.
// - reboot/setSchedule: no-ops on the web (logged).
// - screenshot: captures the current media frame via canvas (cached media is
//   same-origin blob: URLs, so the canvas isn't tainted).

const MEDIA_CACHE = "gocityscreens-player-media-v1";

const localStorageSecureStore: SecureStore = {
  async get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  async set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* quota / private mode — best effort */
    }
  },
  async remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

function cacheKey(mediaId: string): string {
  return `/__media__/${mediaId}`;
}

export function createWebBridge(platform: Platform = "web"): DeviceBridge {
  // media_id -> object URL, created once and reused.
  const objectUrls = new Map<string, string>();

  async function openCache(): Promise<Cache | null> {
    if (typeof caches === "undefined") return null;
    try {
      return await caches.open(MEDIA_CACHE);
    } catch {
      return null;
    }
  }

  return {
    platform,
    secureStore: localStorageSecureStore,

    async downloadMedia(mediaId, url) {
      const cache = await openCache();
      if (!cache) return; // no Cache Storage -> rely on live network in resolve
      const existing = await cache.match(cacheKey(mediaId));
      if (existing) return;
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) throw new Error(`download failed (${res.status}) for ${mediaId}`);
      await cache.put(cacheKey(mediaId), res.clone());
    },

    async getCachedMediaUrl(mediaId) {
      const cached = objectUrls.get(mediaId);
      if (cached) return cached;
      const cache = await openCache();
      if (!cache) return null;
      const res = await cache.match(cacheKey(mediaId));
      if (!res) return null;
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      objectUrls.set(mediaId, objUrl);
      return objUrl;
    },

    async hasCachedMedia(mediaId) {
      const cache = await openCache();
      if (!cache) return false;
      return (await cache.match(cacheKey(mediaId))) != null;
    },

    async clearCache(keep) {
      const cache = await openCache();
      if (!cache) return;
      const keepSet = new Set((keep ?? []).map(cacheKey));
      const keys = await cache.keys();
      for (const req of keys) {
        const url = new URL(req.url);
        if (!keepSet.has(url.pathname)) await cache.delete(req);
      }
      for (const [id, objUrl] of objectUrls) {
        if (!keep || !keep.includes(id)) {
          URL.revokeObjectURL(objUrl);
          objectUrls.delete(id);
        }
      }
    },

    async reboot() {
      console.warn("[webBridge] reboot() not supported on web");
    },

    async screenshot() {
      // Capture the currently-rendered media frame to a PNG. Cached media
      // resolves to same-origin blob: URLs, so the canvas isn't tainted and
      // toBlob() succeeds; for an un-cached cross-origin frame the draw taints
      // the canvas and toBlob throws — we catch it and return null (ack failed).
      try {
        const el = document.querySelector(
          ".media",
        ) as HTMLVideoElement | HTMLImageElement | null;
        if (!el) return null;
        const w =
          (el as HTMLVideoElement).videoWidth ||
          (el as HTMLImageElement).naturalWidth ||
          el.clientWidth;
        const h =
          (el as HTMLVideoElement).videoHeight ||
          (el as HTMLImageElement).naturalHeight ||
          el.clientHeight;
        if (!w || !h) return null;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(el as CanvasImageSource, 0, 0, w, h);
        return await new Promise<Blob | null>((resolve) =>
          canvas.toBlob((b) => resolve(b), "image/png"),
        );
      } catch (err) {
        console.warn("[webBridge] screenshot() capture failed", err);
        return null;
      }
    },

    async setSchedule(on) {
      console.warn(`[webBridge] setSchedule(${on}) not supported on web`);
    },

    async getStorageInfo(): Promise<StorageInfo> {
      try {
        if (navigator.storage?.estimate) {
          const est = await navigator.storage.estimate();
          const used = est.usage ?? 0;
          const quota = est.quota ?? 0;
          return { usedBytes: used, freeBytes: Math.max(0, quota - used) };
        }
      } catch {
        /* ignore */
      }
      return {};
    },

    async getHardwareFingerprint() {
      return null; // native shells return a real hardware id
    },
  };
}
